import { useQuery } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';

import { db_GetSurveyResponseQuestionsAndAnswersRow, db_ListQuestionsBySurveyIDRow, Surveys } from '../../api';
import InsetText from '../../components/govuk/InsetText';
import Markdown from '../../components/Markdown';
import LoadingPage from '../LoadingPage';

export const getSurveyResponseQuestionsAndAnswersQuery = (id: number) => ({
  queryKey: ['survey-responses', id],
  queryFn: async () => Surveys.getSurveyResponseQuestionsAndAnswers(id),
});

export const getSurveyQuestionsBySurveyIDQuery = (surveyID: number) => ({
  queryKey: ['surveys', surveyID, 'questions'],
  queryFn: async () => Surveys.getSurveyQuestionsBySurveyId(surveyID),
});

const RICH_TEXT_TAG_PATTERN = /<(?:p|br|strong|em|ul|ol|li|u|b|i|h[2-4]|blockquote|span)\b[^>]*>/i;

function isRichTextAnswer(answer: string): boolean {
  return RICH_TEXT_TAG_PATTERN.test(answer);
}

export function AnswerContent({ answer }: { answer?: string }) {
  if (!answer || !answer.trim().length) {
    return <>No answer provided yet</>;
  }
  if (answer === 'true') return <>Yes</>;
  if (answer === 'false') return <>No</>;

  if (isRichTextAnswer(answer)) {
    // Survey answers are sanitised on write by util.SanitizeHTML (bluemonday allowlist, no attributes).
    // eslint-disable-next-line @eslint-react/dom-no-dangerously-set-innerhtml
    return <div style={{ overflowWrap: 'break-word' }} dangerouslySetInnerHTML={{ __html: answer }} />;
  }

  return (
    <ReactMarkdown
      components={{
        pre: ({ children, ...props }) => <span {...props}>{children}</span>,
        code({ children, ...props }) {
          return <span {...props}>{children}</span>;
        },
      }}
    >
      {answer}
    </ReactMarkdown>
  );
}

type SurveyResponseOutputProps = {
  surveyResponse?: (db_GetSurveyResponseQuestionsAndAnswersRow | db_ListQuestionsBySurveyIDRow)[];
};

export function SurveyResponseOutput({ surveyResponse }: SurveyResponseOutputProps) {
  return (
    <>
      {surveyResponse?.map((obj, i) => {
        return (
          <div key={i}>
            <strong>
              <Markdown>{obj.question_text}</Markdown>
            </strong>
            <Markdown>{obj.hint}</Markdown>
            <InsetText>
              <AnswerContent answer={obj.answer} />
            </InsetText>
          </div>
        );
      })}
    </>
  );
}

type SurveyResponseViewProps = {
  surveyResponseID: number;
  surveyID?: number;
};

export function SurveyResponseView({ surveyResponseID, surveyID }: SurveyResponseViewProps) {
  const normalizedSurveyID = surveyID ?? 0;

  const { data: surveyResponse, isLoading: surveyResponseIsLoading } = useQuery({
    ...getSurveyResponseQuestionsAndAnswersQuery(surveyResponseID),
    enabled: surveyResponseID !== 0,
  });

  const { data: surveyQuestions, isLoading: surveyQuestionsIsLoading } = useQuery({
    ...getSurveyQuestionsBySurveyIDQuery(normalizedSurveyID),
    enabled: surveyResponseID === 0 && normalizedSurveyID !== 0,
  });

  if (surveyResponseIsLoading || surveyQuestionsIsLoading) return <LoadingPage />;

  const displayRows = surveyResponseID !== 0 ? surveyResponse : surveyQuestions;

  const responseWithFallbackAnswers = displayRows?.map((obj) => ({
    ...obj,
    answer: obj.answer,
  }));

  return <SurveyResponseOutput surveyResponse={responseWithFallbackAnswers} />;
}
